export const FREQUENCY = {
	DAILY: "DAILY",
	WEEKLY: "WEEKLY",
	MONTHLY: "MONTHLY",
	YEARLY: "YEARLY",
};

export const WEEKDAY_CODES = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

export const WEEKDAY_LABELS = { MON: "Mon", TUE: "Tue", WED: "Wed", THU: "Thu", FRI: "Fri", SAT: "Sat", SUN: "Sun" };

const WEEKDAY_PLURAL = { MON: "Mondays", TUE: "Tuesdays", WED: "Wednesdays", THU: "Thursdays", FRI: "Fridays", SAT: "Saturdays", SUN: "Sundays" };

// Quartz day-of-week numbering: 1 = SUN ... 7 = SAT
const WEEKDAY_NAME_TO_QUARTZ = { SUN: 1, MON: 2, TUE: 3, WED: 4, THU: 5, FRI: 6, SAT: 7 };

export const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const pad2 = (num) => String(num).padStart(2, "0");

export const formatTime = (hour, minute) => `${pad2(hour)}:${pad2(minute)}`;

export const daysInMonth = (month, year = new Date().getFullYear()) => new Date(year, month, 0).getDate();

// Recurring yearly schedules should always allow 29 Feb (it just skips non-leap years at run time),
// so the builder caps against a fixed leap year rather than the current year.
export const maxDayForMonth = (month) => daysInMonth(month, 2000);

const isPlainNumber = (value) => /^\d{1,2}$/.test(value);
const isNumberList = (value) => value.split(",").every(isPlainNumber);
const isWeekdayList = (value) => value.split(",").every((code) => WEEKDAY_CODES.includes(code));

export const parseCronToBuilder = (expression) => {
	const fallback = { frequency: null, unsupported: true, expression };

	if (!expression || typeof expression !== "string") return fallback;

	const parts = expression.trim().split(/\s+/);
	if (parts.length !== 6) return fallback;

	const [second, minute, hour, day, month, weekday] = parts;
	if (second !== "0") return fallback;

	// Yearly: specific month + specific day + no weekday
	if (weekday === "?" && isPlainNumber(month) && isPlainNumber(day) && isPlainNumber(hour) && isPlainNumber(minute)) {
		const monthValue = parseInt(month, 10);
		const dayValue = parseInt(day, 10);
		if (monthValue >= 1 && monthValue <= 12 && dayValue >= 1 && dayValue <= daysInMonth(monthValue)) {
			return {
				frequency: FREQUENCY.YEARLY,
				yearly: { month: monthValue, day: dayValue, time: formatTime(parseInt(hour, 10), parseInt(minute, 10)) },
			};
		}
	}

	// Weekly: day = "?", month = "*", weekday = list of day codes
	if (day === "?" && month === "*" && isWeekdayList(weekday) && isPlainNumber(hour) && isPlainNumber(minute)) {
		const days = weekday.split(",");
		if (days.length) {
			return {
				frequency: FREQUENCY.WEEKLY,
				weekly: { days, time: formatTime(parseInt(hour, 10), parseInt(minute, 10)) },
			};
		}
	}

	// Daily: day = "*", month = "*", weekday = "?", hour = list of numbers
	if (day === "*" && month === "*" && weekday === "?" && isNumberList(hour) && isPlainNumber(minute)) {
		const hours = [...new Set(hour.split(",").map((h) => parseInt(h, 10)))]
			.filter((h) => h >= 0 && h <= 23)
			.sort((a, b) => a - b);

		if (hours.length) {
			const minuteValue = parseInt(minute, 10);
			return {
				frequency: FREQUENCY.DAILY,
				daily: { times: hours.map((h) => formatTime(h, minuteValue)) },
			};
		}
	}

	// Monthly: month = "*", weekday = "?", day = "L" or specific number
	if (month === "*" && weekday === "?" && (day === "L" || isPlainNumber(day)) && isPlainNumber(hour) && isPlainNumber(minute)) {
		const lastDay = day === "L";
		const dayValue = lastDay ? null : parseInt(day, 10);
		if (lastDay || (dayValue >= 1 && dayValue <= 31)) {
			return {
				frequency: FREQUENCY.MONTHLY,
				monthly: { day: dayValue, lastDay, time: formatTime(parseInt(hour, 10), parseInt(minute, 10)) },
			};
		}
	}

	return fallback;
};

const splitTime = (time) => {
	const [hour, minute] = (time || "00:00").split(":").map((v) => parseInt(v, 10));
	return { hour: hour || 0, minute: minute || 0 };
};

export const buildCronFromValues = (frequency, values) => {
	if (frequency === FREQUENCY.DAILY) {
		const times = values.daily.times.filter(Boolean);
		const hours = [...new Set(times.map((t) => splitTime(t).hour))].sort((a, b) => a - b);
		return `0 0 ${hours.join(",")} * * ?`;
	}

	if (frequency === FREQUENCY.WEEKLY) {
		const { hour, minute } = splitTime(values.weekly.time);
		const days = WEEKDAY_CODES.filter((code) => values.weekly.days.includes(code));
		return `0 ${minute} ${hour} ? * ${days.join(",")}`;
	}

	if (frequency === FREQUENCY.MONTHLY) {
		const { hour, minute } = splitTime(values.monthly.time);
		const dayPart = values.monthly.lastDay ? "L" : values.monthly.day;
		return `0 ${minute} ${hour} ${dayPart} * ?`;
	}

	if (frequency === FREQUENCY.YEARLY) {
		const { hour, minute } = splitTime(values.yearly.time);
		return `0 ${minute} ${hour} ${values.yearly.day} ${values.yearly.month} ?`;
	}

	return "";
};

export const describeCronSchedule = (parsed) => {
	if (!parsed || parsed.unsupported) return "";

	if (parsed.frequency === FREQUENCY.DAILY) {
		return `Daily at ${parsed.daily.times.join(", ")}`;
	}

	if (parsed.frequency === FREQUENCY.WEEKLY) {
		const { days, time } = parsed.weekly;
		const label = days.length === 1 ? WEEKDAY_PLURAL[days[0]] : days.map((d) => WEEKDAY_LABELS[d]).join(", ");
		return `${label} at ${time}`;
	}

	if (parsed.frequency === FREQUENCY.MONTHLY) {
		const { day, lastDay, time } = parsed.monthly;
		return `${lastDay ? "Last day" : `Day ${day}`} monthly at ${time}`;
	}

	if (parsed.frequency === FREQUENCY.YEARLY) {
		const { day, month, time } = parsed.yearly;
		return `${day} ${MONTH_NAMES[month - 1]} at ${time}`;
	}

	return "";
};

const nextRunDaily = (times, now) => {
	const entries = times.map((t) => splitTime(t)).sort((a, b) => a.hour * 60 + a.minute - (b.hour * 60 + b.minute));

	for (let dayOffset = 0; dayOffset <= 1; dayOffset++) {
		const date = new Date(now);
		date.setDate(date.getDate() + dayOffset);
		date.setHours(0, 0, 0, 0);

		for (const { hour, minute } of entries) {
			const candidate = new Date(date);
			candidate.setHours(hour, minute, 0, 0);
			if (candidate > now) return candidate;
		}
	}

	return null;
};

const nextRunWeekly = (days, time, now) => {
	const { hour, minute } = splitTime(time);

	for (let dayOffset = 0; dayOffset <= 7; dayOffset++) {
		const date = new Date(now);
		date.setDate(date.getDate() + dayOffset);
		date.setHours(0, 0, 0, 0);

		const code = WEEKDAY_CODES[(date.getDay() + 6) % 7];
		if (!days.includes(code)) continue;

		const candidate = new Date(date);
		candidate.setHours(hour, minute, 0, 0);
		if (candidate > now) return candidate;
	}

	return null;
};

const nextRunMonthly = (day, lastDay, time, now) => {
	const { hour, minute } = splitTime(time);

	for (let monthOffset = 0; monthOffset <= 24; monthOffset++) {
		const base = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
		const year = base.getFullYear();
		const month = base.getMonth();
		const monthDayCount = new Date(year, month + 1, 0).getDate();
		const targetDay = lastDay ? monthDayCount : day;

		if (!lastDay && day > monthDayCount) continue;

		const candidate = new Date(year, month, targetDay, hour, minute, 0, 0);
		if (candidate > now) return candidate;
	}

	return null;
};

const nextRunYearly = (month, day, time, now) => {
	const { hour, minute } = splitTime(time);

	for (let yearOffset = 0; yearOffset <= 5; yearOffset++) {
		const year = now.getFullYear() + yearOffset;
		const monthDayCount = new Date(year, month, 0).getDate();
		if (day > monthDayCount) continue;

		const candidate = new Date(year, month - 1, day, hour, minute, 0, 0);
		if (candidate > now) return candidate;
	}

	return null;
};

const expandCronField = (field, isWeekday) => {
	if (field === "*" || field === "?") return null;

	const result = new Set();

	field.split(",").forEach((part) => {
		let step = 1;
		let range = part;

		if (part.includes("/")) {
			const [r, s] = part.split("/");
			range = r;
			step = parseInt(s, 10);
		}

		const toValue = (value) => (isWeekday && /[A-Z]/i.test(value) ? WEEKDAY_NAME_TO_QUARTZ[value.toUpperCase()] : parseInt(value, 10));

		let start;
		let end;

		if (range === "*") {
			start = isWeekday ? 1 : 0;
			end = isWeekday ? 7 : 59;
		} else if (range.includes("-")) {
			const [a, b] = range.split("-");
			start = toValue(a);
			end = toValue(b);
		} else {
			start = end = toValue(range);
		}

		for (let v = start; v <= end; v += step) result.add(v);
	});

	return result;
};

const genericNextRun = (expression, now) => {
	const parts = expression.trim().split(/\s+/);
	if (parts.length !== 6) return null;

	const [, minuteField, hourField, dayField, monthField, weekdayField] = parts;
	const minutes = expandCronField(minuteField, false);
	const hours = expandCronField(hourField, false);
	const days = dayField === "L" ? "L" : expandCronField(dayField, false);
	const months = expandCronField(monthField, false);
	const weekdays = expandCronField(weekdayField, true);
	const hourList = hours ? [...hours].sort((a, b) => a - b) : [...Array(24).keys()];
	const minuteList = minutes ? [...minutes].sort((a, b) => a - b) : [...Array(60).keys()];

	for (let dayOffset = 0; dayOffset <= 800; dayOffset++) {
		const date = new Date(now);
		date.setDate(date.getDate() + dayOffset);
		date.setHours(0, 0, 0, 0);

		const year = date.getFullYear();
		const month = date.getMonth() + 1;
		const dayOfMonth = date.getDate();
		const monthDayCount = new Date(year, month, 0).getDate();

		if (months && !months.has(month)) continue;

		if (days === "L") {
			if (dayOfMonth !== monthDayCount) continue;
		} else if (days && !days.has(dayOfMonth)) continue;

		if (weekdays) {
			const quartzDay = date.getDay() + 1;
			if (!weekdays.has(quartzDay)) continue;
		}

		for (const hour of hourList) {
			for (const minute of minuteList) {
				const candidate = new Date(year, month - 1, dayOfMonth, hour, minute, 0, 0);
				if (candidate > now) return candidate;
			}
		}
	}

	return null;
};

export const getNextRunDate = (cronExpression, enabled, now = new Date()) => {
	if (!enabled || !cronExpression) return null;

	const parsed = parseCronToBuilder(cronExpression);

	if (parsed.frequency === FREQUENCY.DAILY) return nextRunDaily(parsed.daily.times, now);
	if (parsed.frequency === FREQUENCY.WEEKLY) return nextRunWeekly(parsed.weekly.days, parsed.weekly.time, now);
	if (parsed.frequency === FREQUENCY.MONTHLY) return nextRunMonthly(parsed.monthly.day, parsed.monthly.lastDay, parsed.monthly.time, now);
	if (parsed.frequency === FREQUENCY.YEARLY) return nextRunYearly(parsed.yearly.month, parsed.yearly.day, parsed.yearly.time, now);

	return genericNextRun(cronExpression, now);
};
